import { Controller } from '@hotwired/stimulus'

// Typeahead for the compose To-field: as you type a name or email, fetch
// matching workspace contacts from the suggestions endpoint and offer them in
// an ARIA combobox listbox. Picking one fills the email field; the field stays
// a plain editable email input, so an address with no contact yet still works.
//
// The endpoint (urlValue) is workspace-scoped and authorized server-side — this
// controller only renders what it returns and never decides who may see whom.
export default class extends Controller {
  static targets = ['input', 'results']
  static values = { url: String, minLength: { type: Number, default: 1 } }

  connect() {
    this.activeIndex = -1
    this.suggestions = []
    // Monotonic request sequence — drop out-of-order responses (a slow-then-fast
    // keystroke could otherwise render stale suggestions). Same guard as
    // segment_preview_controller; no AbortController precedent in this codebase.
    this.seq = 0
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener('click', this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.debounce)
  }

  // Debounced fetch on every keystroke.
  search() {
    clearTimeout(this.debounce)
    const query = this.inputTarget.value.trim()
    if (query.length < this.minLengthValue) return this.close()

    this.debounce = setTimeout(() => this.fetchSuggestions(query), 200)
  }

  async fetchSuggestions(query) {
    const seq = ++this.seq
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    let matches
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) return this.close()
      matches = await response.json()
    } catch {
      return this.close()
    }
    if (seq !== this.seq) return // a newer keystroke already fired — ignore this stale response
    this.suggestions = matches
    this.render()
  }

  render() {
    if (this.suggestions.length === 0) return this.close()

    this.resultsTarget.innerHTML = this.suggestions
      .map((contact, index) => this.optionMarkup(contact, index))
      .join('')
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute('aria-expanded', 'true')
    this.activeIndex = -1
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  // A name line over a muted email, or just the email when no name is known.
  optionMarkup(contact, index) {
    const id = `contact-suggestion-${index}`
    const email = this.escape(contact.email)
    const name = contact.name
      ? `<span class="fw-medium d-block">${this.escape(contact.name)}</span>`
      : ''
    const emailClass = contact.name ? 'small text-body-secondary' : ''
    return `
      <li id="${id}" role="option" class="list-group-item list-group-item-action" data-email="${email}"
          data-action="mousedown->contact-autocomplete#choose" data-index="${index}">
        ${name}<span class="${emailClass}">${email}</span>
      </li>`
  }

  // Keyboard navigation over the open listbox.
  navigate(event) {
    if (this.resultsTarget.hidden) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.moveActive(-1)
        break
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault()
          this.select(this.suggestions[this.activeIndex])
        }
        break
      case 'Escape':
        this.close()
        break
    }
  }

  moveActive(step) {
    const options = Array.from(this.resultsTarget.children)
    if (options.length === 0) return

    this.activeIndex = (this.activeIndex + step + options.length) % options.length
    options.forEach((option, index) => {
      const active = index === this.activeIndex
      option.classList.toggle('active', active)
      option.setAttribute('aria-selected', active ? 'true' : 'false')
    })
    this.inputTarget.setAttribute('aria-activedescendant', options[this.activeIndex].id)
  }

  // mousedown (not click) so selection lands before the input's blur closes us.
  choose(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    this.select(this.suggestions[index])
  }

  select(contact) {
    if (!contact) return
    this.inputTarget.value = contact.email
    this.close()
    this.inputTarget.focus()
  }

  close() {
    this.resultsTarget.hidden = true
    this.resultsTarget.innerHTML = ''
    this.activeIndex = -1
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  escape(value) {
    const span = document.createElement('span')
    span.textContent = value ?? ''
    return span.innerHTML
  }
}
